import React from 'react';
import { BrowserRouter, Routes, Route, Outlet } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from 'sonner';
import { AuthProvider } from './context/AuthContext';
import { GiwahsProvider } from './context/GiwahsContext';
import ProtectedRoute from './components/ProtectedRoute';
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import Home from './pages/Home';
import ProductDirectory from './pages/ProductDirectory';
import FactoryDirectory from './pages/FactoryDirectory';
import BrandFactoryIntelligence from './pages/BrandFactoryIntelligence';
import SupplierProfile from './pages/SupplierProfile';
import ProductDetail from './pages/ProductDetail';
import RFQForm from './pages/RFQForm';
import Contact from './pages/Contact';
import About from './pages/About';
import AIImageSearch from './pages/AIImageSearch';
import CompareSuppliers from './pages/CompareSuppliers';
import SavedSuppliers from './pages/SavedSuppliers';
import AIAssistant from './pages/AIAssistant';
import ForSuppliers from './pages/ForSuppliers';
import TrustScorePage from './pages/TrustScore';
import SourcingGuides from './pages/SourcingGuides';
import GuideDetail from './pages/GuideDetail';
import CategoryLanding from './pages/CategoryLanding';
import CountrySourcing from './pages/CountrySourcing';
import WhyGiwahs from './pages/WhyGiwahs';
import LegalPage from './pages/LegalPage';
import Login from './pages/Login';
import Signup from './pages/Signup';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import EmailPreview from './pages/EmailPreview';
import RfqTracking from './pages/RfqTracking';
import TrackRfq from './pages/TrackRfq';
import AdminRfqs from './pages/AdminRfqs';
import AdminSupplierClaims from './pages/AdminSupplierClaims';
import AdminVerifications from './pages/AdminVerifications';
import VerificationLadder from './pages/VerificationLadder';
import SupplierVerification from './pages/SupplierVerification';
import Membership from './pages/Membership';
import FounderDashboard from './pages/FounderDashboard';
import AdminMemberships from './pages/AdminMemberships';
import FoundingPartners from './pages/FoundingPartners';
import FoundingPartnersApply from './pages/FoundingPartnersApply';
import TrustCentre from './pages/TrustCentre';
import TrustSecurity from './pages/TrustSecurity';
import TrustPrivacy from './pages/TrustPrivacy';
import TrustCompliance from './pages/TrustCompliance';
import Status from './pages/Status';
import PressKit from './pages/PressKit';
import TrustChipPreview from './pages/TrustChipPreview';
import FoundersWall from './pages/FoundersWall';
import PaymentsDownloadCentre from './pages/PaymentsDownloadCentre';
import AdminRoute from './components/AdminRoute';
import SupplierRoute from './components/SupplierRoute';
import { SupplierClaim, SupplierDashboard, SupplierInbox, SupplierRfqDetail, SupplierMyProfile } from './pages/SupplierPortal';
import NotFound from './pages/NotFound';
import ErrorBoundary from './components/ErrorBoundary';
import ScrollManager from './components/ScrollManager';
import './App.css';
import { usePageviewTracking } from './hooks/usePageview';

const Layout = () => {
  usePageviewTracking();
  return (
    <div className="App flex min-h-screen flex-col">
      <Navbar />
      <main className="flex-1">
        <ErrorBoundary>
          <Outlet />
        </ErrorBoundary>
      </main>
      <Footer />
      <Toaster position="bottom-right" />
    </div>
  );
};

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <ScrollManager />
        <AuthProvider>
          <GiwahsProvider>
            <Routes>
              <Route element={<Layout />}>
                <Route path="/" element={<Home />} />
                <Route path="/products" element={<ProductDirectory />} />
                <Route path="/product/:slug" element={<ProductDetail />} />
                <Route path="/factories" element={<FactoryDirectory />} />
                <Route path="/supplier/:slug" element={<SupplierProfile />} />
                <Route path="/intelligence" element={<BrandFactoryIntelligence />} />
                <Route path="/rfq" element={<RFQForm />} />
                <Route path="/rfq/:reference_code" element={<RfqTracking />} />
                <Route path="/track" element={<TrackRfq />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/about" element={<About />} />
                <Route path="/ai-search" element={<AIImageSearch />} />
                <Route path="/assistant" element={<AIAssistant />} />
                <Route path="/for-suppliers" element={<ForSuppliers />} />
                <Route path="/trust-score" element={<TrustScorePage />} />
                <Route path="/sourcing-guides" element={<SourcingGuides />} />
                <Route path="/sourcing-guides/:slug" element={<GuideDetail />} />
                <Route path="/category/:slug" element={<CategoryLanding />} />
                <Route path="/manufacturers/:country" element={<CountrySourcing />} />
                <Route path="/why-giwahs" element={<WhyGiwahs />} />
                <Route path="/terms" element={<LegalPage pageKey="terms" />} />
                <Route path="/privacy" element={<LegalPage pageKey="privacy" />} />
                <Route path="/supplier-disclaimer" element={<LegalPage pageKey="supplier-disclaimer" />} />
                <Route path="/brand-association-disclaimer" element={<LegalPage pageKey="brand-association-disclaimer" />} />

                {/* Auth — public */}
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<Signup />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />

                {/* Auth-protected buyer pages */}
                <Route path="/saved" element={<ProtectedRoute><SavedSuppliers /></ProtectedRoute>} />
                <Route path="/compare" element={<ProtectedRoute><CompareSuppliers /></ProtectedRoute>} />

                {/* Admin Operations Center */}
                <Route path="/admin/rfqs" element={<AdminRoute><AdminRfqs /></AdminRoute>} />
                <Route path="/admin/suppliers/claims" element={<AdminRoute><AdminSupplierClaims /></AdminRoute>} />
                <Route path="/admin/verifications" element={<AdminRoute><AdminVerifications /></AdminRoute>} />
                <Route path="/admin/memberships" element={<AdminRoute><AdminMemberships /></AdminRoute>} />

                {/* Phase 1 — Founding Partner Commercial Engine */}
                <Route path="/membership" element={<Membership />} />
                <Route path="/founder/dashboard" element={<ProtectedRoute><FounderDashboard /></ProtectedRoute>} />

                {/* Founding Partner — review-only marketing surfaces (Docs 32, 35) */}
                <Route path="/founding-partners" element={<FoundingPartners />} />
                <Route path="/founding-partners/apply" element={<FoundingPartnersApply />} />

                {/* Trust Centre — review-only marketing surfaces (Docs 37–40) */}
                <Route path="/trust" element={<TrustCentre />} />
                <Route path="/trust/security" element={<TrustSecurity />} />
                <Route path="/trust/privacy" element={<TrustPrivacy />} />
                <Route path="/trust/compliance" element={<TrustCompliance />} />
                <Route path="/status" element={<Status />} />
                <Route path="/press" element={<PressKit />} />
                <Route path="/internal/trust-chip-preview" element={<TrustChipPreview />} />
                <Route path="/founders/wall" element={<FoundersWall />} />
                <Route path="/internal/assets/payments" element={<PaymentsDownloadCentre />} />

                {/* Verification — public ladder + supplier flow */}
                <Route path="/factories/verification" element={<VerificationLadder />} />
                <Route path="/supplier/verification" element={<SupplierRoute><SupplierVerification /></SupplierRoute>} />

                {/* Supplier Portal */}
                <Route path="/supplier/claim"          element={<SupplierClaim />} />
                <Route path="/supplier/dashboard"      element={<SupplierRoute><SupplierDashboard /></SupplierRoute>} />
                <Route path="/supplier/inbox"          element={<SupplierRoute><SupplierInbox /></SupplierRoute>} />
                <Route path="/supplier/inbox/:reference_code" element={<SupplierRoute><SupplierRfqDetail /></SupplierRoute>} />
                <Route path="/supplier/profile"        element={<SupplierRoute><SupplierMyProfile /></SupplierRoute>} />

                {/* Internal — email QA. Hidden, noindex, not linked. */}
                <Route path="/dev/email-preview" element={<EmailPreview />} />
                <Route path="/dev/email-preview/:kind" element={<EmailPreview />} />

                {/* 404 — must remain last inside <Layout />.  Any unmatched URL renders the branded NotFound surface. */}
                <Route path="*" element={<NotFound />} />
              </Route>
            </Routes>
          </GiwahsProvider>
        </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
